import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/track";

const HERO_IMG =
  "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3Njk5ODAxfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // iter 64: when login fails because email isn't verified, show a CTA to
  // navigate to /verify-email/resend instead of just a red error message.
  const [needsVerification, setNeedsVerification] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerification(null);
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      if (res.email_not_verified) {
        setNeedsVerification(res.email || form.email);
      } else {
        toast.error(res.error || t("auth.err_invalid_credentials"));
      }
      return;
    }
    toast.success(`${t("nav.dashboard")} — ${res.user.name}`);
    trackEvent("login.success", { role: res.user.role });
    // Role-based redirect
    let next = location.state?.from?.pathname;
    if (!next || next === "/login" || next === "/register") {
      next = res.user.role === "admin" ? "/admin" : "/dashboard";
    }
    navigate(next, { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      {/* Left visual */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/40 to-black/85" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/">
            <Logo showText={false} />
          </Link>
          <div>
            <div className="font-cormorant italic text-zinc-300 text-3xl leading-tight max-w-md">
              {t("auth.login_testimonial_quote")}
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mt-4">
              {t("auth.login_testimonial_author")}
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between lg:justify-end mb-8">
            <div className="lg:hidden">
              <Link to="/">
                <Logo showText={false} />
              </Link>
            </div>
            <LanguageToggle />
          </div>

          <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
            {t("auth.login_title")}
          </div>
          <h1 className="font-outfit font-black text-white text-4xl tracking-tighter leading-tight mb-2">
            {t("auth.login_subtitle")}
          </h1>
          <p className="text-zinc-400 mb-10">
            {t("landing.hero_subtitle").substring(0, 100)}...
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-zinc-300 text-xs uppercase tracking-[0.2em]">
                {t("common.email")}
              </Label>
              <Input
                id="email"
                type="text"
                required
                autoComplete="username"
                placeholder="user@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-zinc-300 text-xs uppercase tracking-[0.2em]"
              >
                {t("common.password")}
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-zinc-900/50 border-white/10 text-white mt-2 h-12"
                data-testid="login-password-input"
              />
            </div>

            {error && !needsVerification && (
              <div
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 px-4 py-3 rounded-md"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            {needsVerification && (
              <div
                className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 px-4 py-3 rounded-md space-y-2"
                data-testid="login-needs-verification"
              >
                <div className="font-medium">{error}</div>
                <Link
                  to={`/verify-email/resend?email=${encodeURIComponent(needsVerification)}`}
                  className="inline-block text-orange-400 hover:text-orange-300 underline text-xs font-medium"
                  data-testid="login-resend-verification-link"
                >
                  {t("auth.verify_resend")} →
                </Link>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  {t("auth.login_button")}
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-5 text-right">
            <ForgotPasswordDialog />
          </div>

          <div className="mt-8 text-sm text-zinc-400">
            {t("auth.login_no_account")}{" "}
            <Link
              to="/register"
              className="text-orange-500 hover:text-orange-400 transition"
              data-testid="login-to-register-link"
            >
              {t("auth.login_create_account")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
